"use client";

import { useCallback, useMemo, useState } from "react";
import { type ChordEntry } from "@/lib/music/chord-catalog";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import { GOLD, VIOLET_SOFT } from "./palette";
import {
  BEATS_PER_MEASURE,
  buildSlotQueue,
  FEEDBACK_VISIBLE_MS,
  type TempoLevel,
} from "./tempo-drill-helpers";
import { useTempoDrill } from "./use-tempo-drill";
import { useResponsiveStep } from "./use-responsive-step";
import { ChordPracticeTempoHeader } from "./chord-practice-tempo-header";
import { ChordPracticeTempoStats } from "./chord-practice-tempo-stats";
import { ChordPracticeTempoStage } from "./chord-practice-tempo-stage";
import { ChordPracticeTempoControls } from "./chord-practice-tempo-controls";
import type { ChordPracticeMode } from "./chord-practice-mode-toggle";

type Props = {
  entry: ChordEntry;
  /** Number of inversions validated in static mode (for the header badge). */
  staticDone?: number;
  mode?: ChordPracticeMode;
  onModeChange?: (mode: ChordPracticeMode) => void;
  onClose?: () => void;
};

const LEVELS: TempoLevel[] = [
  { bpm: 50, label: "Lent", tier: "Échauffement", color: "#7a7a85" },
  { bpm: 70, label: "Confortable", tier: "Apprenti", color: "#a78bfa" },
  { bpm: 90, label: "Standard", tier: "Confirmé", color: "#8b5cf6" },
  { bpm: 110, label: "Rapide", tier: "Avancé", color: "#7c3aed" },
  { bpm: 130, label: "Soutenu", tier: "Virtuose", color: GOLD },
];

export function ChordPracticeTempo({
  entry,
  staticDone = 0,
  mode = "tempo",
  onModeChange,
  onClose,
}: Props) {
  const rootPc = useMemo(() => ROOT_PITCH_CLASS[entry.root], [entry.root]);
  const numNotes = entry.intervals.length;
  // Cap at 3 inversions max.
  const positions = Math.min(numNotes, 3);

  const [levelIdx, setLevelIdx] = useState(2);
  const level = LEVELS[levelIdx];

  const step = useResponsiveStep();
  const drill = useTempoDrill({ entry, level, positions, step });

  const queue = useMemo(
    () => buildSlotQueue(drill.posIdx, positions, entry.intervals, rootPc, entry.root),
    [drill.posIdx, positions, entry.intervals, rootPc, entry.root],
  );

  const setLevel = useCallback(
    (idx: number) => {
      setLevelIdx(idx);
      drill.reset();
    },
    [drill],
  );

  // Drill measure number, count-in beats are excluded from the numbering;
  // drill beats start right after the count-in measure.
  const drillBeatsElapsed = Math.max(0, drill.countInDone - BEATS_PER_MEASURE);
  const drillMeasure = drill.drillActive
    ? Math.floor(drillBeatsElapsed / BEATS_PER_MEASURE) + 1
    : 0;

  const isFlashing =
    drill.lastResult !== null &&
    Date.now() - drill.lastResult.ts < FEEDBACK_VISIBLE_MS;

  let ambientBg: string;
  if (drill.lastResult?.type === "miss") {
    ambientBg = `radial-gradient(ellipse at 50% 40%, rgba(248, 113, 113, 0.18) 0%, transparent 55%)`;
  } else if (drill.lastResult?.type === "perfect") {
    ambientBg = `radial-gradient(ellipse at 50% 40%, rgba(251, 191, 36, 0.18) 0%, transparent 55%)`;
  } else {
    ambientBg = `radial-gradient(ellipse at 50% 40%, ${VIOLET_SOFT} 0%, transparent 55%)`;
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* Ambient glow, color reflects last result */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: ambientBg, transition: "background 300ms" }}
      />

      <ChordPracticeTempoHeader
        entry={entry}
        positions={positions}
        staticDone={staticDone}
        mode={mode}
        onModeChange={onModeChange}
        onClose={onClose}
      />

      <ChordPracticeTempoStats
        combo={drill.combo}
        bestCombo={drill.bestCombo}
        hits={drill.hits}
        misses={drill.misses}
      />

      <ChordPracticeTempoStage
        beat={drill.beat}
        running={drill.running}
        inCountIn={drill.inCountIn}
        drillActive={drill.drillActive}
        countInDone={drill.countInDone}
        drillMeasure={drillMeasure}
        numNotes={numNotes}
        rootPc={rootPc}
        queue={queue}
        slideOffset={drill.slideOffset}
        animating={drill.animating}
        lastResult={drill.lastResult}
        isFlashing={isFlashing}
        recentEvents={drill.recentEvents}
        step={step}
      />

      <ChordPracticeTempoControls
        levels={LEVELS}
        levelIdx={levelIdx}
        running={drill.running}
        onReset={drill.reset}
        onTogglePlay={drill.togglePlay}
        onSelectLevel={setLevel}
      />

      <style>{`
        @keyframes tempoFlash {
          from { transform: scale(0.85); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
